import React, { useState, useEffect } from 'react';
import { BarChart3, Users, Activity, Calendar, Loader2, AlertCircle } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Button } from './ui/button';
import apiService from '../services/api';
import { useBrandSettings } from '../context/BrandSettingsContext';

/**
 * Admin-only anonymized global history view.
 * Shows aggregate stats (concentration analysis, verdict distribution, daily trend)
 * and a browsable list of ALL user searches with opaque per-user pseudonyms.
 *
 * Backed by GET /api/fact-check/history/global.
 */
const TIME_WINDOWS = [
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
  { value: 0, label: 'All time' },
];

const TIER_COLOR = {
  admin: 'bg-purple-100 text-purple-800 border-purple-300 dark:bg-purple-900/40 dark:text-purple-200',
  pro: 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/40 dark:text-blue-200',
  premium: 'bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/40 dark:text-blue-200',
  enterprise: 'bg-indigo-100 text-indigo-800 border-indigo-300 dark:bg-indigo-900/40 dark:text-indigo-200',
  free: 'bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-300',
  anonymous: 'bg-slate-100 text-slate-500 border-slate-300 dark:bg-slate-800 dark:text-slate-400',
};

const AdminGlobalHistoryPanel = () => {
  const { getVerdictClass } = useBrandSettings();
  const [days, setDays] = useState(30);
  const [includeAdmin, setIncludeAdmin] = useState(false);
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({
          days: String(days),
          include_admin: String(includeAdmin),
          limit: '100',
        });
        const res = await apiService.request(`/fact-check/history/global?${params.toString()}`);
        setData(res);
      } catch (e) {
        setError(e?.message || 'Failed to load global history');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [days, includeAdmin]);

  const stats = data?.stats;
  const items = data?.fact_checks || [];

  // ---- concentration metric: what % of SIGNED-IN searches did the top 5 users account for? ----
  // Excludes anonymous-session searches from both numerator and denominator so the metric
  // reflects "power users vs long-tail" rather than getting swamped by null-user_id rows.
  const topFivePct = (() => {
    if (!stats || !stats.top_users?.length) return null;
    const signedInSearches = (stats.total_searches || 0) - (stats.anonymous_session_searches || 0);
    if (signedInSearches <= 0) return null;
    const topFiveSum = stats.top_users.reduce((s, u) => s + (u.count || 0), 0);
    return Math.round((topFiveSum / signedInSearches) * 100);
  })();

  const verdictBadgeClass = (verdict) => {
    const c = getVerdictClass(verdict);
    if (c === 'true') return 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/30 dark:text-green-300';
    if (c === 'false') return 'bg-red-100 text-red-800 border-red-300 dark:bg-red-900/30 dark:text-red-300';
    return 'bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900/30 dark:text-yellow-300';
  };

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="admin-global-history-panel">
      {/* Filters row */}
      <Card className="border-purple-200 dark:border-purple-900/50">
        <CardContent className="p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-500 dark:text-slate-400" />
              <select
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
                className="text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 px-2 py-1"
                data-testid="global-history-time-filter"
              >
                {TIME_WINDOWS.map((w) => (
                  <option key={w.value} value={w.value}>{w.label}</option>
                ))}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
              <input
                type="checkbox"
                checked={includeAdmin}
                onChange={(e) => setIncludeAdmin(e.target.checked)}
                className="rounded"
                data-testid="global-history-include-admin"
              />
              Include admin searches
            </label>
            {stats && !includeAdmin && stats.admin_searches_excluded > 0 && (
              <span className="text-xs text-slate-500 dark:text-slate-400">
                (excluding {stats.admin_searches_excluded} admin search{stats.admin_searches_excluded === 1 ? '' : 'es'})
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {error && (
        <Alert variant="destructive" data-testid="global-history-error">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-16" data-testid="global-history-loading">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600 dark:text-blue-400" />
        </div>
      )}

      {!isLoading && stats && (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <Card data-testid="stat-total-searches">
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-500 dark:text-slate-400 mb-1">
                  <Activity className="w-4 h-4" /> Total Searches
                </div>
                <div className="text-2xl sm:text-3xl font-bold text-slate-800 dark:text-slate-100">
                  {stats.total_searches.toLocaleString()}
                </div>
                {stats.anonymous_session_searches > 0 && (
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-1" data-testid="stat-anonymous-session-count">
                    incl. {stats.anonymous_session_searches.toLocaleString()} session-only
                  </div>
                )}
              </CardContent>
            </Card>
            <Card data-testid="stat-unique-users">
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-500 dark:text-slate-400 mb-1">
                  <Users className="w-4 h-4" /> Unique Users
                </div>
                <div className="text-2xl sm:text-3xl font-bold text-slate-800 dark:text-slate-100">
                  {stats.unique_users.toLocaleString()}
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  signed-in only
                </div>
              </CardContent>
            </Card>
            <Card data-testid="stat-avg-per-user">
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-500 dark:text-slate-400 mb-1">
                  <BarChart3 className="w-4 h-4" /> Avg / User
                </div>
                <div className="text-2xl sm:text-3xl font-bold text-slate-800 dark:text-slate-100">
                  {stats.unique_users > 0
                    ? ((stats.total_searches - (stats.anonymous_session_searches || 0)) / stats.unique_users).toFixed(1)
                    : '0'}
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">signed-in only</div>
              </CardContent>
            </Card>
            <Card data-testid="stat-top-five-pct">
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-500 dark:text-slate-400 mb-1">
                  Top-5 share
                </div>
                <div className="text-2xl sm:text-3xl font-bold text-slate-800 dark:text-slate-100">
                  {topFivePct !== null ? `${topFivePct}%` : '—'}
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">of signed-in searches</div>
              </CardContent>
            </Card>
          </div>

          {/* Top users + verdict distribution row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
            <Card data-testid="top-users-card">
              <CardContent className="p-3 sm:p-4">
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4" /> Top Users by Activity
                </h3>
                {stats.top_users.length === 0 ? (
                  <div className="text-xs text-slate-500 dark:text-slate-400">No signed-in user activity in this window.</div>
                ) : (
                  <>
                  <ul className="space-y-2">
                    {stats.top_users.map((u, i) => {
                      const signedInTotal = (stats.total_searches || 0) - (stats.anonymous_session_searches || 0);
                      const pct = signedInTotal > 0 ? Math.round((u.count / signedInTotal) * 100) : 0;
                      return (
                        <li key={u.pseudonym + i} className="flex items-center gap-3 text-sm" data-testid={`top-user-row-${i}`}>
                          <span className="w-5 text-xs text-slate-400 dark:text-slate-500 font-mono">#{i + 1}</span>
                          <code className="text-xs font-mono text-slate-700 dark:text-slate-300">{u.pseudonym}</code>
                          <Badge className={`text-xs ${TIER_COLOR[u.tier] || TIER_COLOR.free}`}>{u.tier}</Badge>
                          <div className="flex-1 mx-2">
                            <div className="h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                              <div className="h-full bg-blue-500 dark:bg-blue-400" style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                          <span className="text-xs text-slate-600 dark:text-slate-400 tabular-nums w-16 text-right">
                            {u.count} · {pct}%
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-3 border-t border-slate-200 dark:border-slate-700 pt-2">
                    % is of signed-in searches only. Anonymous session-only searches are counted separately in the Total Searches card.
                  </div>
                  </>
                )}
              </CardContent>
            </Card>

            <Card data-testid="verdict-distribution-card">
              <CardContent className="p-3 sm:p-4">
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3">Verdict Distribution</h3>
                {Object.keys(stats.verdict_distribution).length === 0 ? (
                  <div className="text-xs text-slate-500 dark:text-slate-400">No verdicts in this window.</div>
                ) : (
                  <ul className="space-y-2">
                    {Object.entries(stats.verdict_distribution)
                      .sort((a, b) => b[1] - a[1])
                      .map(([verdict, count]) => {
                        const pct = stats.total_searches > 0 ? Math.round((count / stats.total_searches) * 100) : 0;
                        return (
                          <li key={verdict} className="flex items-center gap-3 text-sm">
                            <Badge className={`text-xs ${verdictBadgeClass(verdict)}`}>{verdict}</Badge>
                            <div className="flex-1 mx-2">
                              <div className="h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                <div className="h-full bg-slate-500 dark:bg-slate-400" style={{ width: `${pct}%` }} />
                              </div>
                            </div>
                            <span className="text-xs text-slate-600 dark:text-slate-400 tabular-nums w-16 text-right">
                              {count} · {pct}%
                            </span>
                          </li>
                        );
                      })}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>

          {/* List of anonymized fact-checks */}
          <Card data-testid="global-history-list">
            <CardContent className="p-3 sm:p-4">
              <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-3">
                Recent Searches ({items.length} shown{data?.total > items.length ? ` of ${data.total}` : ''})
              </h3>
              {items.length === 0 ? (
                <div className="text-sm text-slate-500 dark:text-slate-400 py-6 text-center">
                  No searches match the current filters.
                </div>
              ) : (
                <ul className="divide-y divide-slate-200 dark:divide-slate-700">
                  {items.map((fc) => (
                    <li key={fc.fact_check_id} className="py-3 flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`global-history-row-${fc.fact_check_id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-slate-800 dark:text-slate-100 truncate" title={fc.claim}>
                          {fc.claim}
                        </div>
                        <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 flex flex-wrap items-center gap-2">
                          <code className="font-mono">{fc.user_pseudonym}</code>
                          <Badge className={`text-xs ${TIER_COLOR[fc.user_tier] || TIER_COLOR.free}`}>{fc.user_tier}</Badge>
                          <span>·</span>
                          <span>{new Date(fc.created_at).toLocaleString()}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 sm:justify-end">
                        <Badge className={`text-xs ${verdictBadgeClass(fc.veracity)}`}>{fc.veracity}</Badge>
                        <span className="text-xs text-slate-500 dark:text-slate-400 tabular-nums">
                          {fc.confidence}%
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default AdminGlobalHistoryPanel;
